import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { includes } from 'lodash';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortAlt } from '@fortawesome/pro-solid-svg-icons';
import { faInfoCircle, faSortAmountUp, faSortAmountDown } from '@fortawesome/pro-solid-svg-icons';

import SelectableRecordTable from 'components/selectable_record_table/selectable_record_table';
import OverlayButton from 'components/table_header/overlay_button';
import ColumnSelector from 'components/table_header/column_selector';
import Tooltip from 'components/tooltip';

import { COLUMN_DEFINITIONS, NAME } from './person_cell';
import PersonRow from './person_row';

export default function PeopleTable({
  cellOverrides,
  columns,
  people,
  defaultColumns,
  sortableColumns,
  sortColumn,
  sortDesc,
  stickyColumns,
  onRemoveContact,
  onSetColumns,
  onSort,
  onUpdateContact
}) {
  const [showScrollBorder, setShowScrollBorder] = useState(false);
  const [showColumnFilters, setShowColumnFilters] = useState(false);

  const hideOverlayFilters = () => setShowColumnFilters(false);
  const showOverlayFilters = () => setShowColumnFilters(true);

  const allColumns = [NAME, ...columns];
  const columnSelectorMapping = defaultColumns.reduce((acc, column) => {
    return { ...acc, [column]: COLUMN_DEFINITIONS[column].title };
  }, {});

  const columnHeaders = allColumns.map((columnName) => {
    let sortIcon;
    const active = sortColumn && sortColumn === columnName;
    const ableToSort = (!sortableColumns || includes(sortableColumns, columnName)) &&
                        COLUMN_DEFINITIONS[columnName].sortValue;
    const tooltip = COLUMN_DEFINITIONS[columnName].tooltip;

    if (sortColumn && ableToSort) {
      let faIcon = faSortAlt;
      if (active) faIcon = sortDesc ? faSortAmountDown : faSortAmountUp;

      sortIcon = <FontAwesomeIcon className="ml-2" icon={faIcon} />;
    }

    return (
      <th
        className={classNames('text-nowrap', { clickable: !!sortIcon, active })}
        key={columnName}
        onClick={ableToSort ? () => onSort(columnName) : undefined}
      >
        {COLUMN_DEFINITIONS[columnName].title}
        {tooltip &&
          <Tooltip
            content={tooltip}
            dark
            placement="bottom"
          >
            <FontAwesomeIcon className="ml-2" icon={faInfoCircle} />
          </Tooltip>}
        {sortIcon}
      </th>
    );
  });

  columnHeaders.push(
    <th className="text-center w-10-px" key="settings">
      <OverlayButton hide={!showColumnFilters} onClose={hideOverlayFilters} onOpen={showOverlayFilters}>
        <ColumnSelector
          currentColumns={columns}
          defaultColumns={defaultColumns}
          informationMappings={
            {
              contactInformation: {
                mappings: columnSelectorMapping,
                key: 'person-information'
              }
            }
          }
          onCancelColumns={hideOverlayFilters}
          onSetColumns={onSetColumns}
        />
      </OverlayButton>
    </th>
  );

  const handleHorizontalScroll = (event) => {
    const shouldShowScrollBorder = event.target.scrollLeft !== 0;

    if (shouldShowScrollBorder !== showScrollBorder) setShowScrollBorder(shouldShowScrollBorder);
  };

  return (
    <div className="contacts-table">
      <div className="table-responsive w-100" name="scroll-container" onScroll={handleHorizontalScroll}>
        <SelectableRecordTable
          className="m-0"
          columnHeaders={columnHeaders}
          hasSelectAll={false}
          hasSelectColumn={false}
          stickyColShadow={showScrollBorder}
          stickyColumns={stickyColumns}
          stickyLastColumn
        >
          {people.map((person) => {
            return (
              <PersonRow
                cellOverrides={cellOverrides}
                columns={allColumns}
                key={person.id}
                name={`row-${person.id}`}
                person={person}
                onRemoveContact={onRemoveContact}
                onUpdateContact={onUpdateContact}
              />
            );
          })}
        </SelectableRecordTable>
      </div>
    </div>
  );
}

PeopleTable.propTypes = {
  cellOverrides: PropTypes.object,
  columns: PropTypes.arrayOf(PropTypes.string).isRequired,
  defaultColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  people: PropTypes.arrayOf(PropTypes.object).isRequired,
  sortableColumns: PropTypes.arrayOf(PropTypes.string),
  sortColumn: PropTypes.string,
  sortDesc: PropTypes.bool,
  stickyColumns: PropTypes.number,
  onRemoveContact: PropTypes.func.isRequired,
  onSetColumns: PropTypes.func.isRequired,
  onSort: PropTypes.func,
  onUpdateContact: PropTypes.func.isRequired
};
